'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useCart } from '@/context/cart-context';
import { formatPrice } from '@/lib/shopify';
import type { Cart, CartItem } from '@/lib/shopify/types';

function variantNumericId(variantGid: string): string | null {
  const id = variantGid.split('/').pop()?.split('?')[0];
  return id && /^\d+$/.test(id) ? id : null;
}

function buildCheckoutPermalink(cart: Cart | null, lines: CartItem[]): string | null {
  if (!cart || lines.length === 0) return null;

  const items = lines
    .map((line) => {
      const variantId = variantNumericId(line.merchandise.id);
      return variantId ? `${variantId}:${line.quantity}` : null;
    })
    .filter(Boolean);

  if (items.length !== lines.length) return cart.checkoutUrl;

  try {
    const checkoutUrl = new URL(cart.checkoutUrl);
    checkoutUrl.pathname = `/cart/${items.join(',')}`;
    checkoutUrl.search = '?checkout&_fd=0';
    return checkoutUrl.toString();
  } catch {
    return `/cart/${items.join(',')}?checkout&_fd=0`;
  }
}

export default function CartPage() {
  const { cart, isLoading, updateItem, removeItem } = useCart();
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const lines = cart?.lines ?? [];
  const confirmLine = lines.find((l) => l.id === confirmRemoveId) ?? null;
  const checkoutHref = buildCheckoutPermalink(cart, lines);

  useEffect(() => {
    const header = document.querySelector('.header') as HTMLElement | null;
    if (!header) return;
    const update = () => {
      document.documentElement.style.setProperty(
        '--cart-header-h',
        `${header.offsetHeight}px`
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (!confirmRemoveId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmRemoveId(null);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [confirmRemoveId]);

  const handleConfirmRemove = async () => {
    if (!confirmRemoveId) return;
    await removeItem(confirmRemoveId);
    setConfirmRemoveId(null);
  };

  const handleCheckout = () => {
    if (!checkoutHref) return;
    window.location.assign(checkoutHref);
  };

  const subtotal = cart?.cost.subtotalAmount;
  const tax = cart?.cost.totalTaxAmount;
  const total = cart?.cost.totalAmount;

  return (
    <section className="cart-page">
      <div className="container">
        <h1 className="cart-title">Your Basket</h1>

        {isLoading && !cart ? (
          <div className="cart-loading" aria-live="polite" aria-busy="true">
            <span className="cart-loading-spinner" aria-hidden="true" />
            <p>Loading your basket…</p>
          </div>
        ) : lines.length === 0 ? (
          <div className="cart-empty">
            <p>Your basket is currently empty.</p>
            <Link href="/" className="cart-empty-btn">Continue Shopping</Link>
          </div>
        ) : (
          <div className="cart-layout">
            <div className="cart-lines-wrap">
              <ul className="cart-lines">
                {lines.map((line) => {
                  const img = line.merchandise.product.featuredImage;
                  const variantTitle = line.merchandise.selectedOptions
                    .filter((o) => o.name.toLowerCase() !== 'title')
                    .map((o) => `${o.name}: ${o.value}`)
                    .join(' · ');
                  return (
                    <li key={line.id} className="cart-line">
                      <div className="cart-line-img">
                        {img?.url ? (
                          <Image
                            src={img.url}
                            alt={img.altText || line.merchandise.product.title}
                            width={200}
                            height={200}
                            sizes="100px"
                          />
                        ) : (
                          <div className="cart-line-img-ph" />
                        )}
                      </div>
                      <div className="cart-line-main">
                        <Link
                          href={`/products/${line.merchandise.product.handle}`}
                          className="cart-line-title"
                        >
                          {line.merchandise.product.title}
                        </Link>
                        {variantTitle && (
                          <p className="cart-line-variant">{variantTitle}</p>
                        )}
                        <div className="cart-line-qty">
                          <button
                            type="button"
                            onClick={() => updateItem(line.id, Math.max(1, line.quantity - 1))}
                            disabled={isLoading || line.quantity <= 1}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span>{line.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateItem(line.id, line.quantity + 1)}
                            disabled={isLoading}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="cart-line-right">
                        <button
                          type="button"
                          className="cart-line-trash"
                          onClick={() => setConfirmRemoveId(line.id)}
                          aria-label="Remove item"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M3 6h18" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                          </svg>
                        </button>
                        <span className="cart-line-price">
                          {formatPrice(line.cost.totalAmount)}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <aside className="cart-summary">
              <h2 className="cart-summary-title">Order Summary</h2>
              <div className="cart-summary-rows">
                <div className="cart-summary-row">
                  <span>Subtotal ({cart?.totalQuantity ?? 0} item{(cart?.totalQuantity ?? 0) === 1 ? '' : 's'})</span>
                  <span>{subtotal ? formatPrice(subtotal) : '—'}</span>
                </div>
                <div className="cart-summary-row">
                  <span>Shipping</span>
                  <span className="cart-summary-muted">Calculated at checkout</span>
                </div>
                {tax && parseFloat(tax.amount) > 0 && (
                  <div className="cart-summary-row">
                    <span>Tax</span>
                    <span>{formatPrice(tax)}</span>
                  </div>
                )}
              </div>
              <div className="cart-summary-total">
                <span>Total</span>
                <span>{total ? formatPrice(total) : '—'}</span>
              </div>
              {checkoutHref && (
                <button
                  type="button"
                  onClick={handleCheckout}
                  className="cart-checkout-btn"
                >
                  Checkout
                </button>
              )}
              <Link href="/" className="cart-continue-link">
                ← Continue Shopping
              </Link>
            </aside>
          </div>
        )}
      </div>

      {confirmLine && (
        <div
          className="cart-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cart-confirm-title"
          onClick={() => setConfirmRemoveId(null)}
        >
          <div className="cart-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="cart-confirm-title" className="cart-confirm-title">
              Remove from basket?
            </h3>
            <p className="cart-confirm-body">
              Are you sure you want to remove{' '}
              <strong>{confirmLine.merchandise.product.title}</strong> from your basket?
            </p>
            <div className="cart-confirm-actions">
              <button
                type="button"
                className="cart-confirm-btn cart-confirm-cancel"
                onClick={() => setConfirmRemoveId(null)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cart-confirm-btn cart-confirm-remove"
                onClick={handleConfirmRemove}
                disabled={isLoading}
              >
                {isLoading ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cart-page {
          padding: 2rem 0 4rem;
          background: var(--color-white);
          min-height: 50vh;
        }
        .cart-page .container { max-width: 1100px; }
        .cart-title {
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 1.5rem;
        }
        .cart-empty {
          text-align: center;
          padding: 3rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          align-items: center;
          color: var(--color-text-muted);
        }
        .cart-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.85rem;
          padding: 4rem 1rem;
          color: var(--color-text-secondary);
          font-size: 0.9rem;
        }
        .cart-loading p { margin: 0; }
        .cart-loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-primary);
          border-radius: 50%;
          animation: cart-spin 0.8s linear infinite;
        }
        @keyframes cart-spin {
          to { transform: rotate(360deg); }
        }
        .cart-empty-btn {
          padding: 0.75rem 1.5rem;
          background: var(--color-accent);
          color: var(--color-primary-dark);
          border-radius: 0.5rem;
          font-weight: 600;
          font-size: 0.9rem;
        }
        .cart-empty-btn:hover { background: var(--color-accent-dark); }

        .cart-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .cart-layout { grid-template-columns: 1fr 340px; }
        }

        .cart-lines-wrap {
          min-width: 0;
        }

        .cart-lines {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .cart-line {
          display: grid;
          grid-template-columns: 100px 1fr auto;
          gap: 1rem;
          padding: 1rem;
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          align-items: start;
          background: var(--color-white);
        }
        .cart-line-img {
          width: 100px;
          height: 100px;
          overflow: hidden;
          border-radius: 0.5rem;
          background: var(--color-white);
        }
        .cart-line-img img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 0.25rem;
          box-sizing: border-box;
        }
        .cart-line-img-ph {
          width: 100%;
          height: 100%;
          background: var(--color-off-white);
        }
        .cart-line-main {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          min-width: 0;
        }
        .cart-line-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--color-text-primary);
          line-height: 1.3;
        }
        .cart-line-title:hover { color: var(--color-primary); }
        .cart-line-variant {
          font-size: 0.8rem;
          color: var(--color-text-muted);
          margin: 0;
        }
        .cart-line-qty {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: 0.4rem;
          padding: 0.15rem 0.5rem;
          width: fit-content;
        }
        .cart-line-qty button {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 700;
          width: 1.5rem;
          height: 1.5rem;
          color: var(--color-primary);
          padding: 0;
        }
        .cart-line-qty button:disabled { opacity: 0.4; cursor: not-allowed; }
        .cart-line-qty span {
          font-size: 0.85rem;
          font-weight: 600;
          min-width: 1.5rem;
          text-align: center;
        }
        .cart-line-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: space-between;
          gap: 0.75rem;
          height: 100%;
        }
        .cart-line-trash {
          background: none;
          border: none;
          padding: 0.35rem;
          color: var(--color-text-muted);
          cursor: pointer;
          border-radius: 0.35rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: color 0.15s, background 0.15s;
        }
        .cart-line-trash:hover {
          color: #cf3f43;
          background: rgba(207, 63, 67, 0.08);
        }
        .cart-line-trash:disabled { opacity: 0.4; cursor: not-allowed; }
        .cart-line-price {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-text-primary);
          white-space: nowrap;
        }

        .cart-summary {
          padding: 1.25rem;
          background: var(--color-off-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          display: flex;
          flex-direction: column;
        }
        @media (min-width: 768px) {
          .cart-summary {
            position: sticky;
            top: calc(var(--cart-header-h, 80px) + 1rem);
            align-self: start;
          }
        }
        .cart-summary-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 1rem;
        }
        .cart-summary-rows {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid var(--color-border);
          margin-bottom: 0.75rem;
        }
        .cart-summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          color: var(--color-text-primary);
        }
        .cart-summary-muted {
          color: var(--color-text-muted);
          font-size: 0.8rem;
        }
        .cart-summary-total {
          display: flex;
          justify-content: space-between;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin-bottom: 1rem;
        }
        .cart-checkout-btn {
          display: block;
          width: 100%;
          text-align: center;
          padding: 0.9rem 1.25rem;
          background: var(--color-accent);
          color: var(--color-primary-dark);
          border: 0;
          border-radius: 0.5rem;
          cursor: pointer;
          font-family: inherit;
          font-weight: 700;
          font-size: 0.95rem;
          margin-bottom: 0.75rem;
          appearance: none;
          transition: background 0.15s;
        }
        .cart-checkout-btn:hover { background: var(--color-accent-dark); }
        .cart-continue-link {
          display: block;
          text-align: center;
          font-size: 0.8rem;
          color: var(--color-text-secondary);
        }
        .cart-continue-link:hover { color: var(--color-primary); }

        .cart-confirm {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: cart-confirm-fade 0.15s ease-out;
        }
        @keyframes cart-confirm-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .cart-confirm-box {
          background: var(--color-white);
          border-radius: 0.75rem;
          padding: 1.5rem;
          max-width: 420px;
          width: 100%;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
        }
        .cart-confirm-title {
          margin: 0 0 0.5rem;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .cart-confirm-body {
          margin: 0 0 1.25rem;
          font-size: 0.9rem;
          color: var(--color-text-secondary);
          line-height: 1.5;
        }
        .cart-confirm-body strong {
          color: var(--color-text-primary);
          font-weight: 700;
        }
        .cart-confirm-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }
        .cart-confirm-btn {
          padding: 0.65rem 1.1rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .cart-confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cart-confirm-cancel {
          background: var(--color-white);
          color: var(--color-text-primary);
          border-color: var(--color-border);
        }
        .cart-confirm-cancel:hover:not(:disabled) {
          background: var(--color-off-white);
        }
        .cart-confirm-remove {
          background: #cf3f43;
          color: var(--color-white);
          border-color: #cf3f43;
        }
        .cart-confirm-remove:hover:not(:disabled) {
          background: #b8363a;
          border-color: #b8363a;
        }
      `}</style>
    </section>
  );
}
